import BoltAndNut from './bolt-and-nut.js'
import Concrete from './concrete.js'
import Geometry from './geometry.js'
import Load from './load.js'
import Steel from './steel.js'
import { getAnchorPropertyIndex } from '../anchor-properties.js'

import AnchorLoadClass from '../legacy/loads/bolt-load.js'
import AnchorGeometryClass from '../legacy/geometry/anchor-geometry.js'
import AnchorConcreteClass from '../legacy/concrete/concrete.js'
import AnchorBoltClass from '../legacy/anchor-bolt/anchor-bolt.js'
import AnchorACIDesignClass from '../legacy/design/anchor-design.js'
import AnchorProblemClass from '../legacy/design/anchor-problem.js'

import * as resultsView from '../results-view.js'

export default class AnchorDesignAdapter {
  boltAndNut = new BoltAndNut()
  concrete = new Concrete()
  geometry = new Geometry()
  load = new Load()
  steel = new Steel()
  createdAt = Date.now()
  modifiedAt = Date.now()
  title = 'New Anchor Bolt Design'
  key = null
  isActive = false

  serialize() {
    this.save()
    return {
      'key': this.key,
      'title': this.title,
      'isActive': this.isActive,
      'createdAt': this.createdAt,
      'modifiedAt': this.modifiedAt,
      'boltAndNut': this.boltAndNut.serialize(),
      'concrete': this.concrete.serialize(),
      'load': this.load.serialize(),
      'steel': this.steel.serialize(),
      'geometry': this.geometry.serialize()
    }
  }

  save() {
    this.boltAndNut.dirty = false
    this.concrete.dirty = false
    this.load.dirty = false
    this.steel.dirty = false
    this.geometry.dirty = false
  }

  deserialize(json) {
    const d = json //JSON.parse(jsonString)
    this.key = d.key
    this.title = d.title
    this.isActive = d.isActive
    this.createdAt = d.createdAt
    this.modifiedAt = d.modifiedAt
    this.boltAndNut.deserialize(d.boltAndNut)
    this.concrete.deserialize(d.concrete)
    this.load.deserialize(d.load)
    this.steel.deserialize(d.steel)
    this.geometry.deserialize(d.geometry)
    //const event = new CustomEvent('model-deserialized', { detail: this });
    //document.dispatchEvent(event)
  }

  get blockWidth() {
    return this.geometry.leftEdge + (this.geometry.nColumns - 1) * this.geometry.columnSpacing + this.geometry.rightEdge
  }

  get blockHeight() {
    return this.geometry.topEdge + (this.geometry.nRows - 1) * this.geometry.rowSpacing + this.geometry.bottomEdge
  }

  get boltPatternWidth() {
    return (this.geometry.nColumns - 1) * this.geometry.columnSpacing
  }

  get boltPatternHeight() {
    return (this.geometry.nRows - 1) * this.geometry.rowSpacing
  }

  get dirty() {
    return this.boltAndNut.dirty || this.concrete.dirty || this.load.dirty || this.steel.dirty || this.geometry.dirty
  }

  legacyLoadFactory() {
    const o = new AnchorLoadClass()
    o.vX = this.load.shearXLoad // kips
    o.vY = this.load.shearYLoad // kips
    o.nZ = this.load.tensileLoad  // kips
    o.categoryCOrHigher = this.concrete.highSeismicZone
    o.factorLoadSource = "ACI 318-08"
    return o
  }

  legacyGeometryFactory() {
    const o = new AnchorGeometryClass()
    o.rows = this.geometry.nRows
    o.columns = this.geometry.nColumns
    o.rowSpacing = this.geometry.rowSpacing // in
    o.colSpacing = this.geometry.columnSpacing // in
    o.topEdge = this.geometry.topEdge // in
    o.bottomEdge = this.geometry.bottomEdge // in
    o.leftEdge = this.geometry.leftEdge // in
    o.rightEdge = this.geometry.rightEdge // in
    return o
  }

  legacyConcreteFactory() {
    const o = new AnchorConcreteClass()
    o.fc = this.concrete.concreteStrength // ksi
    o.thickness = this.concrete.concreteThickness // in
    o.barsCrossTensionFailure = this.steel.barsCrossTensionFailure
    o.barsPreventTensionBreakout = this.steel.barsPreventTensionBreakout
    o.shearXNumber4Bars = this.steel.shearXNumber4Bars
    o.shearXStirrups = this.steel.shearXStirrups
    o.shearXNoBreakout = this.steel.shearXNoBreakout
    o.shearYNumber4Bars = this.shearYNumber4Bars
    o.shearYStirrups = this.shearYStirrups
    o.shearYNoBreakout = this.shearYNoBreakout
    return o
  }

  legacyAnchorBoltFactory() {
    const o = new AnchorBoltClass()
    o.diameterIndex = getAnchorPropertyIndex(this.boltAndNut.boltType, this.boltAndNut.boltDiameterLabel)
    o.anchorType = this.boltAndNut.boltTypeStr
    o.nutTypeIndex = 0 // unused 
    o.nutType = this.boltAndNut.nutType
    o.embedDepth = this.boltAndNut.embedDepth // in
    o.hookLength = this.boltAndNut.hookLength // in
    o.tensileStrength = this.boltAndNut.tensileStrength // ksi
    o.anchorsWelded = this.boltAndNut.anchorsWelded
    o.groutPads = this.boltAndNut.groutPads
    o.fc = this.concrete.concreteStrength // ksi
    o.thickness = this.concrete.concreteThickness // in
    o.barsCrossTensionFailure = this.steel.barsCrossTensionFailure
    o.barsPreventTensionBreakout = this.steel.barsPreventTensionBreakout
    o.shearXNumber4Bars = this.steel.shearXNumber4Bars
    o.shearXStirrups = this.steel.shearXStirrups
    o.shearXNoBreakout = this.steel.shearXNoBreakout
    o.shearYNumber4Bars = this.shearYNumber4Bars
    o.shearYStirrups = this.shearYStirrups
    o.shearYNoBreakout = this.shearYNoBreakout
    return o
  }

  updateDesign() {
    const anchorDesign = new AnchorACIDesignClass()
    const boltAndNut = this.legacyAnchorBoltFactory()
    const load = this.legacyLoadFactory()
    const concrete = this.legacyConcreteFactory()
    const geometry = this.legacyGeometryFactory()
    const anchorProblem = new AnchorProblemClass()
    anchorProblem.CalculateTensionCapacity(anchorDesign, load, boltAndNut, geometry, concrete)
    anchorProblem.CalculateShearXCapacity(anchorDesign, load, boltAndNut, geometry, concrete)
    anchorProblem.CalculateShearYCapacity(anchorDesign, load, boltAndNut, geometry, concrete)
    anchorProblem.CalculateInteractionValueX(anchorDesign, load, boltAndNut, geometry, concrete)
    anchorProblem.CalculateInteractionValueY(anchorDesign, load, boltAndNut, geometry, concrete)

    resultsView.showSummaryCheck(anchorProblem)
    resultsView.showTensionCheckSummary(anchorProblem)
    resultsView.showShearXSummary(anchorProblem)
    resultsView.showShearYSummary(anchorProblem)
    resultsView.showInteractionCalcs(anchorProblem, load)
    resultsView.showTensionCapacityCalcs(anchorProblem, boltAndNut, geometry)
    resultsView.showShearCalcs(anchorProblem, boltAndNut, geometry)
  }
}